<!-- Shared inline icons (kept local — these have no equivalent in assets/images/svg/). -->
<!-- The AI mark (Zeplin): a large four-point sparkle with a small one top-right. -->
<ng-template #sparkleIcon>
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    class="w-full h-full"
  >
    <path
      d="M10 5c.4 3 1 4.9 2.1 6 1.1 1.1 3 1.7 6 2.1.5.1.5.7 0 .8-3 .4-4.9 1-6 2.1-1.1 1.1-1.7 3-2.1 6-.1.5-.7.5-.8 0-.4-3-1-4.9-2.1-6-1.1-1.1-3-1.7-6-2.1-.5-.1-.5-.7 0-.8 3-.4 4.9-1 6-2.1C8.2 9.9 8.8 8 9.2 5c.1-.5.7-.5.8 0z"
    />
    <path
      d="M19 1.6c.2 1.3.5 2.1 1 2.6.5.5 1.3.8 2.6 1 .3.1.3.4 0 .5-1.3.2-2.1.5-2.6 1-.5.5-.8 1.3-1 2.6-.1.3-.4.3-.5 0-.2-1.3-.5-2.1-1-2.6-.5-.5-1.3-.8-2.6-1-.3-.1-.3-.4 0-.5 1.3-.2 2.1-.5 2.6-1 .5-.5.8-1.3 1-2.6.1-.3.4-.3.5 0z"
    />
  </svg>
</ng-template>

<!-- Floating trigger (FEC-1227): always on screen while the panel is closed — no longer a footer link. -->
@if (!open()) {
  <button
    type="button"
    aria-label="Open AI Help"
    class="ai-help-trigger fixed z-40 flex items-center gap-2 h-12 pl-3 pr-4 rounded-full bg-dark dark:bg-white text-white dark:text-dark text-sm font-semibold shadow-lg cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-dark dark:focus-visible:ring-white"
    (click)="aiHelpService.open()"
  >
    <span class="w-6 h-6 shrink-0"
      ><ng-container *ngTemplateOutlet="sparkleIcon"></ng-container
    ></span>
    AI Help
  </button>
}

<ng-template #chevronDownIcon>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
    class="w-full h-full"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
</ng-template>

<!-- Outline thumbs-up (Zeplin "Was this helpful?"); rotated 180° for thumbs-down. -->
<ng-template #thumbIcon>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
    class="w-full h-full"
  >
    <path
      d="M7.48 18.35l3.1 2.4c.4.4 1.3.6 1.9.6h3.8c1.2 0 2.5-.9 2.8-2.1l2.4-7.3c.5-1.4-.4-2.6-1.9-2.6h-4c-.6 0-1.1-.5-1-1.2l.5-3.2c.2-.9-.4-1.9-1.3-2.2-.8-.3-1.8.1-2.2.7l-4.1 6.1"
    />
    <path
      d="M2.38 18.35v-9.8c0-1.4.6-1.9 2-1.9h1c1.4 0 2 .5 2 1.9v9.8c0 1.4-.6 1.9-2 1.9h-1c-1.4 0-2-.5-2-1.9z"
    />
  </svg>
</ng-template>

<!-- Outline copy icon — two overlapping rounded squares. -->
<ng-template #copyIcon>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
    class="w-full h-full"
  >
    <rect x="8" y="8" width="13" height="13" rx="3" />
    <path d="M16 8V6a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h2" />
  </svg>
</ng-template>

<ng-template #chevronRightIcon>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
    class="w-full h-full"
  >
    <polyline points="9 6 15 12 9 18"></polyline>
  </svg>
</ng-template>

<!-- Backdrop — fades in; click minimizes (keeps the conversation, same as the chevron). -->
<!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
<div
  class="fixed inset-0 bg-black/50 z-50"
  [class.transition-opacity]="animationsReady()"
  [class.duration-300]="animationsReady()"
  [class.opacity-100]="open()"
  [class.opacity-0]="!open()"
  [class.pointer-events-none]="!open()"
  [attr.aria-hidden]="!open()"
  (click)="minimize()"
></div>

<aside
  class="ai-help-panel z-[60] flex flex-col bg-white dark:bg-surface-dark ease-in-out"
  [class.transition-transform]="animationsReady()"
  [class.duration-300]="animationsReady()"
  [class.ai-help-panel--closed]="!open()"
  role="dialog"
  aria-modal="true"
  aria-label="AI Help"
  [attr.inert]="!open() ? '' : null"
  cdkTrapFocus
  [cdkTrapFocusAutoCapture]="open()"
  (keydown.escape)="minimize()"
>
  <!-- Header -->
  <div class="flex items-center justify-between gap-3 px-4 pt-5 pb-4">
    <div class="flex items-center gap-3 min-w-0">
      <span
        class="flex items-center justify-center w-10 h-10 p-2 rounded-xl bg-dark dark:bg-white text-white dark:text-dark shrink-0"
      >
        <ng-container *ngTemplateOutlet="sparkleIcon"></ng-container>
      </span>
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <p class="text-base font-semibold text-dark dark:text-white">AI Help</p>
          <span
            class="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide bg-dark text-white dark:bg-white dark:text-dark"
            >AI</span
          >
        </div>
        <p class="flex items-center gap-1.5 text-xs text-dark-grey dark:text-grey truncate mt-0.5">
          <span class="w-1.5 h-1.5 rounded-full bg-secondary shrink-0"></span>
          Helping with {{ pageContext().label }}
        </p>
      </div>
    </div>
    <div class="flex items-center gap-2 shrink-0">
      <button
        type="button"
        aria-label="Minimize AI Help"
        class="flex items-center justify-center w-10 h-10 rounded-full bg-muted dark:bg-surface-dark-muted cursor-pointer text-dark dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
        (click)="minimize()"
      >
        <span class="w-4 h-4"
          ><ng-container *ngTemplateOutlet="chevronDownIcon"></ng-container
        ></span>
      </button>
      <button
        type="button"
        aria-label="Close AI Help"
        class="flex items-center justify-center w-10 h-10 rounded-full bg-muted dark:bg-surface-dark-muted cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
        (click)="close()"
      >
        <app-icon name="close" [size]="20" class="text-dark dark:text-white" />
      </button>
    </div>
  </div>

  <!-- Divider -->
  <div class="h-[2px] bg-gradient-to-r from-primary via-primary-dark/70 to-transparent"></div>

  <!-- Tabs -->
  <div class="ai-help-tabs flex items-center gap-2 px-4 py-3.5 overflow-x-auto">
    @for (tab of tabs; track tab.id) {
      <button
        type="button"
        class="shrink-0 h-[30px] px-2.5 rounded-full text-[13px] font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
        [class.bg-dark]="activeTab() === tab.id"
        [class.text-white]="activeTab() === tab.id"
        [class.dark:bg-white]="activeTab() === tab.id"
        [class.dark:text-dark]="activeTab() === tab.id"
        [class.bg-muted]="activeTab() !== tab.id"
        [class.text-dark]="activeTab() !== tab.id"
        [class.dark:bg-surface-dark-muted]="activeTab() !== tab.id"
        [class.dark:text-grey]="activeTab() !== tab.id"
        [attr.aria-pressed]="activeTab() === tab.id"
        (click)="selectTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    }
  </div>

  <!-- Content -->
  <div class="flex-1 min-h-0 overflow-y-auto px-4">
    @if (activeTab() === 'browse') {
      @if (hasConversation()) {
        <ng-container *ngTemplateOutlet="conversationThread"></ng-container>
      } @else if (browseCategory(); as category) {
        <button
          type="button"
          class="flex items-center gap-1 text-sm text-dark dark:text-white mb-3 cursor-pointer"
          (click)="backToCategories()"
        >
          <span class="w-4 h-4 rotate-180"
            ><ng-container *ngTemplateOutlet="chevronRightIcon"></ng-container
          ></span>
          Browse
        </button>
        <div
          class="flex items-center gap-3 rounded-xl bg-muted dark:bg-surface-dark-muted p-4 mb-4"
        >
          <span
            class="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-surface-dark shrink-0"
          >
            <app-icon [name]="category.icon" [size]="20" class="text-dark dark:text-white" />
          </span>
          <div class="min-w-0">
            <p class="text-base font-semibold text-dark dark:text-white truncate">
              {{ category.label }}
            </p>
            <p class="text-xs text-dark-grey dark:text-grey">
              {{ category.questions.length }} question{{
                category.questions.length === 1 ? '' : 's'
              }}
              ·
              {{ pageContext().label }}
            </p>
          </div>
        </div>
        <div
          class="divide-y divide-default dark:divide-dark-grey rounded-xl border border-solid border-default dark:border-dark-grey mb-4"
        >
          @for (question of category.questions; track trackByText($index, question)) {
            <ng-container
              *ngTemplateOutlet="questionRow; context: { $implicit: question }"
            ></ng-container>
          }
        </div>
        <p class="text-xs text-dark-grey dark:text-grey pb-4">
          Tap a question for a step-by-step answer, or ask your own below.
        </p>
      } @else {
        <p class="text-sm text-dark dark:text-grey mb-4">
          Browse help by area. Topics for the page you're on are listed first.
        </p>
        @if (onThisPageCategories().length) {
          <p class="text-xs font-semibold tracking-wide text-dark dark:text-grey mb-2">
            ON THIS PAGE · {{ pageContext().label.toUpperCase() }}
          </p>
          <div
            class="divide-y divide-default dark:divide-dark-grey rounded-xl border border-solid border-default dark:border-dark-grey mb-4"
          >
            @for (category of onThisPageCategories(); track trackByCategory($index, category)) {
              <ng-container
                *ngTemplateOutlet="categoryRow; context: { $implicit: category }"
              ></ng-container>
            }
          </div>
        }
        <p class="text-xs font-semibold tracking-wide text-dark dark:text-grey mb-2">
          ALL HELP TOPICS
        </p>
        <div
          class="divide-y divide-default dark:divide-dark-grey rounded-xl border border-solid border-default dark:border-dark-grey mb-4"
        >
          @for (category of allTopicsCategories(); track trackByCategory($index, category)) {
            <ng-container
              *ngTemplateOutlet="categoryRow; context: { $implicit: category }"
            ></ng-container>
          }
        </div>
      }
    } @else {
      @if (hasConversation()) {
        <ng-container *ngTemplateOutlet="conversationThread"></ng-container>
      } @else {
        <div class="rounded-xl bg-muted dark:bg-surface-dark-muted p-4 mt-1 mb-5 flex gap-3">
          <span
            class="flex items-center justify-center w-8 h-8 p-1.5 rounded-lg border border-solid border-default dark:border-dark-grey bg-white dark:bg-surface-dark text-dark dark:text-white shrink-0"
          >
            <ng-container *ngTemplateOutlet="sparkleIcon"></ng-container>
          </span>
          <div>
            <p class="text-sm font-semibold text-dark dark:text-white mb-1">Hi {{ firstName() }}</p>
            <p class="text-sm text-dark dark:text-grey">{{ tabConfig().welcomeBody }}</p>
          </div>
        </div>

        @if (tabConfig().primaryQuestions.length) {
          <p class="text-xs font-semibold tracking-wide text-dark dark:text-grey mb-2">
            {{ tabConfig().primaryLabel }}
          </p>
          <div
            class="divide-y divide-default dark:divide-dark-grey rounded-xl border border-solid border-default dark:border-dark-grey mb-5"
          >
            @for (question of tabConfig().primaryQuestions; track trackByText($index, question)) {
              <ng-container
                *ngTemplateOutlet="questionRow; context: { $implicit: question }"
              ></ng-container>
            }
          </div>
        }

        @if (tabConfig().secondaryLabel && tabConfig().secondaryQuestions.length) {
          <p class="text-xs font-semibold tracking-wide text-dark dark:text-grey mb-2">
            {{ tabConfig().secondaryLabel }}
          </p>
          <div
            class="divide-y divide-default dark:divide-dark-grey rounded-xl border border-solid border-default dark:border-dark-grey mb-5"
          >
            @for (question of tabConfig().secondaryQuestions; track trackByText($index, question)) {
              <ng-container
                *ngTemplateOutlet="questionRow; context: { $implicit: question }"
              ></ng-container>
            }
          </div>
        }
      }
    }
  </div>

  <!-- Bottom input bar -->
  <div
    class="px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-solid border-default dark:border-dark-grey"
  >
    <!-- Composer (Zeplin): input, "Asking as" and Send share one bordered card. -->
    <div
      class="mb-2 rounded-2xl border border-solid border-default dark:border-dark-grey bg-white dark:bg-surface-dark px-4 pt-3 pb-2.5 focus-within:border-dark dark:focus-within:border-white transition-colors"
    >
      <app-text-input
        [ngModel]="inputValue()"
        (ngModelChange)="inputValue.set($event)"
        (enter)="submitFreeText()"
        [placeholder]="currentPlaceholder()"
        ariaLabel="Ask AI Help"
        className="!h-6 !p-0 !border-[0px] !rounded-none !text-sm !bg-transparent"
      ></app-text-input>
      <div class="flex items-center justify-between mt-3">
        <app-dropdown-menu [items]="askingAsItems()" placement="top-start" menuWidth="w-32">
          <span
            class="flex items-center gap-1 px-3 py-1.5 rounded-full bg-muted dark:bg-surface-dark-muted text-xs text-dark dark:text-grey cursor-pointer"
          >
            Asking as
            <span class="font-semibold text-dark dark:text-white">{{
              askingAs() === 'agent' ? 'Agent' : 'Admin'
            }}</span>
            <span class="w-3 h-3"
              ><ng-container *ngTemplateOutlet="chevronDownIcon"></ng-container
            ></span>
          </span>
        </app-dropdown-menu>
        <button
          type="button"
          aria-label="Send question"
          class="flex items-center justify-center w-[30px] h-[30px] rounded-full bg-dark dark:bg-white text-white dark:text-dark disabled:cursor-default cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-dark dark:focus-visible:ring-white"
          [disabled]="!inputValue().trim() || thinking()"
          (click)="submitFreeText()"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            focusable="false"
            class="w-4 h-4"
          >
            <path
              d="M7.4 6.32l8.49-2.83c3.81-1.27 5.88.81 4.62 4.62l-2.83 8.49c-1.9 5.71-5.02 5.71-6.92 0l-.84-2.52-2.52-.84c-5.71-1.9-5.71-5.01 0-6.92z"
            />
            <path d="M10.11 13.65l3.58-3.59" />
          </svg>
        </button>
      </div>
    </div>
    <p class="text-[11px] text-center text-dark-grey dark:text-grey">
      Answers are based on Agent Access help content. Contact support if you need further help.
    </p>
  </div>
</aside>

<!-- A single Browse category row — reused for both "on this page" and "all topics". -->
<ng-template #categoryRow let-category>
  <button
    type="button"
    class="flex items-center gap-3 w-full px-4 py-3 text-left cursor-pointer hover:bg-muted dark:hover:bg-surface-dark-muted"
    (click)="openCategory(category)"
  >
    <span
      class="flex items-center justify-center w-7 h-7 rounded-full bg-muted dark:bg-surface-dark-muted shrink-0"
    >
      <app-icon [name]="category.icon" [size]="14" class="text-dark dark:text-white" />
    </span>
    <span class="flex-1 text-sm text-dark dark:text-white">{{ category.label }}</span>
    <span class="text-xs text-dark-grey dark:text-grey shrink-0"
      >{{ category.questions.length }} question{{
        category.questions.length === 1 ? '' : 's'
      }}</span
    >
    <span class="w-4 h-4 shrink-0 text-dark-grey dark:text-grey"
      ><ng-container *ngTemplateOutlet="chevronRightIcon"></ng-container
    ></span>
  </button>
</ng-template>

<!-- A single suggested-question row — used across Ask / For Agents / For Admins groups. -->
<ng-template #questionRow let-question>
  <button
    type="button"
    class="flex items-center gap-3 w-full px-4 py-3 text-left cursor-pointer hover:bg-muted dark:hover:bg-surface-dark-muted"
    (click)="askQuestion(question)"
  >
    <span
      class="flex items-center justify-center w-7 h-7 rounded-full bg-muted dark:bg-surface-dark-muted shrink-0"
    >
      <app-icon [name]="question.icon" [size]="14" class="text-dark dark:text-white" />
    </span>
    <span class="flex-1 text-sm text-dark dark:text-white">{{ question.text }}</span>
    <span class="w-4 h-4 shrink-0 text-dark-grey dark:text-grey"
      ><ng-container *ngTemplateOutlet="chevronRightIcon"></ng-container
    ></span>
  </button>
</ng-template>

<!-- The Q&A thread — shared by Ask / Browse / For Agents / For Admins once a question's been asked. -->
<ng-template #conversationThread>
  <div class="pt-4">
    @for (turn of conversation(); track $index; let i = $index) {
      <div class="flex justify-end mb-3">
        <div
          class="max-w-[85%] bg-dark text-white dark:bg-white dark:text-dark rounded-2xl rounded-br-sm px-4 py-2.5 text-sm"
        >
          {{ turn.question }}
        </div>
      </div>

      <div class="mb-6">
        <div class="flex items-center gap-2 mb-2">
          <span
            class="flex items-center justify-center w-6 h-6 p-1 rounded-full bg-dark dark:bg-white text-white dark:text-dark shrink-0"
          >
            <ng-container *ngTemplateOutlet="sparkleIcon"></ng-container>
          </span>
          <span class="text-sm font-semibold text-dark dark:text-white">AI Help</span>
          <span
            class="px-2 py-0.5 rounded-full text-[11px] bg-muted dark:bg-surface-dark-muted text-dark dark:text-grey"
            >{{ turn.perspectiveLabel }}</span
          >
        </div>

        <p class="text-sm text-dark dark:text-white mb-3">{{ turn.answer.summary }}</p>

        @if (turn.answer.steps.length) {
          <ol class="space-y-3 mb-3">
            @for (step of turn.answer.steps; track $index; let stepIndex = $index) {
              <li class="flex gap-3">
                <span
                  class="flex items-center justify-center w-5 h-5 mt-px rounded-full bg-dark dark:bg-white text-white dark:text-dark text-[11px] font-semibold shrink-0"
                  >{{ stepIndex + 1 }}</span
                >
                <div>
                  <p class="text-sm font-medium text-dark dark:text-white">{{ step.title }}</p>
                  <p class="text-sm text-dark-grey dark:text-grey">{{ step.detail }}</p>
                </div>
              </li>
            }
          </ol>
        }

        @if (turn.answer.screenshotCaption) {
          <div
            class="mb-3 rounded-lg border border-solid border-default dark:border-dark-grey bg-muted dark:bg-surface-dark-muted p-3"
          >
            <div
              class="h-16 rounded border border-dashed border-grey dark:border-dark-grey bg-white dark:bg-surface-dark"
            ></div>
            <p class="flex items-center gap-1.5 mt-2 text-xs text-dark-grey dark:text-grey">
              <app-icon name="image-file" [size]="14" />
              {{ turn.answer.screenshotCaption }}
            </p>
          </div>
        }

        @if (turn.answer.tip) {
          <div
            class="flex items-start gap-2 mb-3 rounded-lg bg-muted dark:bg-surface-dark-muted px-3 py-2.5"
          >
            <app-icon
              name="info-circle"
              [size]="16"
              class="mt-0.5 text-dark-grey dark:text-grey shrink-0"
            />
            <p class="text-xs text-dark-grey dark:text-grey">Tip: {{ turn.answer.tip }}</p>
          </div>
        }

        @if (turn.answer.steps.length || turn.answer.screenshotCaption) {
          <div class="flex items-center gap-3 mb-4">
            <span class="text-xs text-dark-grey dark:text-grey">Was this helpful?</span>
            <button
              type="button"
              aria-label="Mark answer as helpful"
              [attr.aria-pressed]="feedback().get(i) === 'up'"
              class="flex items-center justify-center w-7 h-7 rounded-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
              [class.bg-muted]="feedback().get(i) === 'up'"
              [class.dark:bg-surface-dark-muted]="feedback().get(i) === 'up'"
              (click)="setFeedback(i, 'up')"
            >
              <span class="w-[18px] h-[18px] text-dark dark:text-white"
                ><ng-container *ngTemplateOutlet="thumbIcon"></ng-container
              ></span>
            </button>
            <button
              type="button"
              aria-label="Mark answer as not helpful"
              [attr.aria-pressed]="feedback().get(i) === 'down'"
              class="flex items-center justify-center w-7 h-7 rounded-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
              [class.bg-muted]="feedback().get(i) === 'down'"
              [class.dark:bg-surface-dark-muted]="feedback().get(i) === 'down'"
              (click)="setFeedback(i, 'down')"
            >
              <span class="w-[18px] h-[18px] rotate-180 text-dark dark:text-white"
                ><ng-container *ngTemplateOutlet="thumbIcon"></ng-container
              ></span>
            </button>
            <button
              type="button"
              aria-label="Copy answer"
              class="flex items-center justify-center w-7 h-7 rounded-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
              (click)="copyAnswer(turn.answer)"
            >
              <span class="w-[18px] h-[18px] text-dark dark:text-white"
                ><ng-container *ngTemplateOutlet="copyIcon"></ng-container
              ></span>
            </button>
          </div>
        }

        @if (turn.answer.related.length) {
          <div>
            <p class="text-xs font-semibold tracking-wide text-dark dark:text-grey mb-2">RELATED</p>
            <div class="flex flex-wrap gap-2">
              @for (related of turn.answer.related; track related) {
                <button
                  type="button"
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-solid border-default dark:border-dark-grey text-sm text-dark dark:text-white cursor-pointer hover:bg-muted dark:hover:bg-surface-dark-muted"
                  (click)="askRelated(related)"
                >
                  {{ related }}
                  <span class="w-3.5 h-3.5"
                    ><ng-container *ngTemplateOutlet="chevronRightIcon"></ng-container
                  ></span>
                </button>
              }
            </div>
          </div>
        }
      </div>
    }
    @if (thinking()) {
      <p class="text-sm text-dark-grey dark:text-grey mb-6" role="status" aria-live="polite">
        Thinking…
      </p>
    }
  </div>
</ng-template>
